// exclusionPreviewFromBase — 「人を外して戻す」のプレビューを、焼きと同じく
// **世代 G の PNG を土台にして G 以降だけ replay する**版
// (exclusion-ui-investigation-2026-10-10.md「③-b」)。
//
// `renderExclusionPreview` (系譜の根から全 replay) との違い:
//   - 土台: G のレイヤ PNG を `EngineCore.applySnapshot` (入室時の snapshot 読み込みと
//     同じ経路) で入れる。`base === null` は白紙から (系譜の根が全消し / 先頭)。
//   - 何を描くか: 共有の `classifyReplayEvent` (サーバの焼き `ExclusionBakeService` と
//     同じ判定) が 'replay' / 'replay-reincluded' としたイベントだけを、seq 順に適用する。
//     外す対象・取り消し済み sid (pair-filter で丸ごと) は描かない。G の時点で描きかけ
//     だった線は G の PNG に途中までの画素が入っているので、pre-G の start/segment から
//     再取り込みして描き直す (= サーバと同じ)。`stroke.cancelled` は 'cancelled' 判定で
//     落ちるので bbox ローカル巻き戻しは使わない。
//   - 中断: イベント 1 件ごとに `signal` を見る (貼り付け画像の取得待ちの後も見る)。
//
// 出力は `renderExclusionPreview` と同じ (縮小済み RGBA + imagepaste の数)。
//
// PNG の decode: 入室時 (`SnapshotLoader`) と同じ `createImageBitmap` +
// `decodeBitmapToRgba` (Canvas2D 経由 = premultiply 往復で半透明画素がずれうる。
// プレビュー用途なので許容、サーバの `@napi-rs/canvas` decode とは差が出得る)。
// `decodeExclusionBaseLayers` がそれ。呼び出し側は結果を (sessionId, seq) で cache できる。

import { EngineCore, UndoBaselineStore } from '@oekaki/drawing-core';
import {
  classifyReplayEvent,
  type ExclusionDecisions,
  type PlanEvent,
  type StrokeLogEvent,
} from '@oekaki/protocol';
import { decodeBitmapToRgba } from '../canvas2d/rasterCanvas.js';
import { type BackgroundColor, DEFAULT_BACKGROUND } from './backgroundAt.js';
import type { ExclusionPreviewResult } from './exclusionPreview.js';
import { computeOutputSize, createFrameScaler, type FrameSize } from './frameScale.js';
import { applyEvent, deriveLayerCount, type ResolveImagepasteFn } from './timelapseReplay.js';

/** イベント何件ごとにイベントループへ手を戻すか (main thread の入力を詰まらせない)。 */
export const PREVIEW_EVENTS_PER_BREATH = 1_024;
/** `onProgress` を呼ぶ最小の増分 (1%)。 */
const PROGRESS_STEP = 0.01;

/** 土台 (G) の decode 済みレイヤ。`rgba` は `canvas` と同じ寸法の straight-α。 */
export interface ExclusionBaseLayer {
  readonly id: number;
  readonly rgba: Uint8ClampedArray;
}

export interface ExclusionPreviewBase {
  readonly layers: readonly ExclusionBaseLayer[];
  /** G の meta.background。 */
  readonly background: BackgroundColor;
}

export interface RenderExclusionPreviewFromBaseInput {
  readonly canvas: FrameSize;
  /** G の土台。`null` = 白紙から (`decisions.base === null` のとき)。 */
  readonly base: ExclusionPreviewBase | null;
  /** 共有の `planExclusionDecisions` の結果。 */
  readonly decisions: ExclusionDecisions;
  /**
   * 判定対象のイベント列 (seq 昇順、**外す前**の列)。G より前も含んでよい
   * (G 時点で描きかけの線の pre-G イベントはここから拾う)。
   */
  readonly events: readonly StrokeLogEvent[];
  readonly maxEdge: number;
  /** `base === null` のときの初期背景。省略時は白。 */
  readonly background?: BackgroundColor;
  readonly resolveImagepaste?: ResolveImagepasteFn;
  /** 0..1。約 1% ごと。 */
  readonly onProgress?: (fraction: number) => void;
  readonly signal?: AbortSignal;
}

const yieldToEventLoop = (): Promise<void> => new Promise((resolve) => setTimeout(resolve, 0));

/** 中断されたとき `null`。 */
export const renderExclusionPreviewFromBase = async (
  input: RenderExclusionPreviewFromBaseInput,
): Promise<ExclusionPreviewResult | null> => {
  const aborted = (): boolean => input.signal?.aborted === true;
  if (aborted()) return null;
  const { canvas, decisions } = input;

  // サーバの bake と同じ判定。replay する分だけ集める (件数 = 進捗の分母)。
  const toReplay: StrokeLogEvent[] = [];
  for (const ev of input.events) {
    const verdict = classifyReplayEvent(decisions, ev as PlanEvent);
    if (verdict === 'replay' || verdict === 'replay-reincluded') toReplay.push(ev);
  }

  let layerCount = deriveLayerCount(toReplay);
  for (const l of input.base?.layers ?? []) layerCount = Math.max(layerCount, l.id + 1);
  const background = input.base?.background ?? input.background ?? DEFAULT_BACKGROUND;
  const core = new EngineCore({
    width: canvas.width,
    height: canvas.height,
    layerCount,
    background,
  });
  if (input.base !== null) {
    core.setBackground(background);
    core.applySnapshot(input.base.layers.map((l) => ({ id: l.id, rgba: l.rgba })));
  }
  const undoStore = new UndoBaselineStore(canvas.width, canvas.height, layerCount);

  let totalImagepastes = 0;
  let skippedImagepastes = 0;
  let lastReported = 0;
  const total = toReplay.length;
  for (let i = 0; i < total; i++) {
    if (aborted()) return null;
    const ev = toReplay[i] as StrokeLogEvent;
    if (ev.t === 'whiteboard.imagepaste' || ev.t === 'whiteboard.region') totalImagepastes++;
    const res = await applyEvent(core, undoStore, ev, undefined, input.resolveImagepaste);
    if (res === 'imagepaste-skipped') skippedImagepastes++;
    if (aborted()) return null;
    const fraction = (i + 1) / total;
    if (fraction - lastReported >= PROGRESS_STEP && i + 1 < total) {
      lastReported = fraction;
      input.onProgress?.(fraction);
    }
    if ((i + 1) % PREVIEW_EVENTS_PER_BREATH === 0) await yieldToEventLoop();
  }
  if (aborted()) return null;

  const composed = core.compositeAllForExport();
  const output = computeOutputSize(canvas.width, canvas.height, input.maxEdge);
  const rgba = createFrameScaler()(
    composed,
    canvas.width,
    canvas.height,
    output.width,
    output.height,
  );
  input.onProgress?.(1);
  return {
    width: output.width,
    height: output.height,
    rgba,
    totalImagepastes,
    skippedImagepastes,
  };
};

export interface DecodeBaseLayersDeps {
  readonly createImageBitmapFn?: (blob: Blob) => Promise<ImageBitmap>;
  readonly decodeBitmapFn?: (bitmap: ImageBitmap) => {
    rgba: Uint8ClampedArray;
    width: number;
    height: number;
  };
}

/**
 * G のレイヤ PNG を土台の画素にする (入室時 `SnapshotLoader` と同じ decode)。
 * 1 枚でも失敗 / 寸法違いなら `null` (= 呼び出し側は根から全 replay の fallback)。
 * 土台は全レイヤが揃っていないと別の絵になるので、入室時のような部分採用はしない。
 */
export const decodeExclusionBaseLayers = async (
  blobs: ReadonlyArray<{ readonly id: number; readonly blob: Blob }>,
  canvas: FrameSize,
  deps: DecodeBaseLayersDeps = {},
): Promise<readonly ExclusionBaseLayer[] | null> => {
  const createBitmap =
    deps.createImageBitmapFn ??
    ((b: Blob): Promise<ImageBitmap> => globalThis.createImageBitmap(b));
  const decode = deps.decodeBitmapFn ?? decodeBitmapToRgba;
  const out: ExclusionBaseLayer[] = [];
  for (const { id, blob } of blobs) {
    let bitmap: ImageBitmap | undefined;
    try {
      bitmap = await createBitmap(blob);
      const d = decode(bitmap);
      if (d.width !== canvas.width || d.height !== canvas.height) return null;
      out.push({ id, rgba: d.rgba });
    } catch {
      return null;
    } finally {
      try {
        bitmap?.close?.();
      } catch {
        // ignore
      }
    }
  }
  return out;
};
